import React from 'react';
import { Card, Col, Row, Button, Form } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useAppDispatch } from "@services/hooks/helperHooks";
import { ReduxStore } from '@interfaces/store';
import { ConsumerUserFull } from '@interfaces/consumer/users';
import { useAuth, useLocalizedBlock, useRole } from '@services/hooks';
import { KeyValueSet } from '@interfaces/helpers';
import { useFormik } from 'formik';
import { toastr } from '@/utils/toastHelper';
import ConsumerUsersService from '@api/consumer/consumerUsersService';
import { consumerActions } from '@store/consumer';

interface ConsumerUserRolesProps {
    user: ConsumerUserFull;
}

const UserRoles: React.FC<ConsumerUserRolesProps> = ({ user }) => {

    const dispatch = useAppDispatch();
    const allRoles = useSelector(({ consumer }: ReduxStore) => consumer.users.allowedRoles.data);
    const targetRoles = user.roles;

    const { user: self } = useAuth();
    const canEdit = useRole('consumer.users.roles.edit') && user.id !== self?.id;

    const roleNames = useLocalizedBlock('roles');
    const lang = useLocalizedBlock('consumer.user.roles');

    const values: KeyValueSet<boolean> = {};

    Object.entries(allRoles).forEach(([key, roles]) => {
        roles.forEach(role => values[role.replace(/\./g, ':')] = targetRoles.includes(role))
    })

    const onSubmit = async (values: KeyValueSet<boolean>) => {
        
        if(!canEdit){
            return;
        }

        const roles: KeyValueSet<boolean> = {};
        Object.entries(values).forEach(([role, value]) => roles[role.replace(/:/g, '.')] = value);
        const api = new ConsumerUsersService();
        const result = await api.changeConsumerUserRoles({
            user: user.id,
            roles
        });

        if(result.ok) {
            toastr.success('Изменение прав', 'Права пользователя успешно сохранены.');
            dispatch(consumerActions.loadUser(user.id, true));
        }
        else {
            if(result.status === 403) {
                toastr.error('Изменение прав', 'У вас нет прав для этой операции.');
            }
            else if(result.errorCode === 'NotFound') {
                toastr.error('Изменение прав', 'Пользователь не найден');
            }
            else if(result.errorCode === 'CantEditYourself') {
                toastr.error('Изменение прав', 'Нельзя редактировать свои права');
            }
            else {
                toastr.error('Изменение прав', 'Что-то пошло не так');
            }
        }
    }

    const formik = useFormik({
        initialValues: values,
        onSubmit
    });

    return (
        <Form onSubmit={formik.handleSubmit} onBlur={formik.handleBlur}>
            <Row>
                {
                    Object.entries(allRoles).map(e => (
                        <Col key={`roles.${e[0]}`}>
                            <Card className="mb-3">
                                <Card.Header as="h6" className="bg-body-tertiary">
                                    {roleNames[`${e[0]}.title`]}
                                </Card.Header>
                                <Card.Body>
                                    {
                                        e[1].map((role) => {
                                            const fRole = role.replace(/\./g, ':');
                                            return (
                                                <Form.Check
                                                    key={`role.${role}`}
                                                    disabled={!canEdit}
                                                    type="checkbox"
                                                    id={role} name={fRole}
                                                    onChange={formik.handleChange}
                                                    checked={formik.values[fRole]}
                                                    value={formik.values[fRole].toString()}
                                                    label={roleNames[`${role}.name`]}
                                                />
                                            )
                                        })
                                    }
                                </Card.Body>
                            </Card>
                        </Col>
                    ))
                }
            </Row>
            {
                canEdit && (
                    <Card>
                        <Card.Body>
                            <Row className="flex-between-center">
                                <Col md>

                                </Col>
                                <Col xs="auto">
                                    <Button variant="primary" type="submit" disabled={!formik.isValid && !formik.isSubmitting}>
                                        {lang['save']}
                                    </Button>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                )
            }
        </Form>
    )
}

export default UserRoles;